import React, { useState } from 'react';
import { Coins, Check, X, Lightning, Crown, Star, CaretDown, CaretUp } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { CREDIT_PACKS, PRICING_TIERS } from '../lib/stripe-config';

interface PricingPageProps {
  onNavigate?: (view: string) => void;
}

// FAQ data
const FAQ_ITEMS = [
  {
    question: 'What are credits used for?',
    answer: 'Credits are used for all AI generation tasks — image generation, character creation, voice synthesis, and more. Each task costs a different amount of credits depending on the model and resolution used.',
  },
  {
    question: 'What happens if I exhaust my monthly credits?',
    answer: 'You can continue using the platform but won\'t be able to generate until your credits refresh next month. Starter and Pro subscribers can purchase add-on credit packs anytime.',
  },
  {
    question: 'Can I upgrade or downgrade my plan?',
    answer: 'Yes! You can upgrade or downgrade at any time from the Settings page. When upgrading, you\'ll be charged the prorated difference. When downgrading, the change takes effect at the end of your current billing period.',
  },
  {
    question: 'Do unused credits carry over?',
    answer: 'Monthly subscription credits do not carry over — they refresh at the start of each billing cycle. However, credits purchased through add-on packs do not expire and remain in your account until used.',
  },
  {
    question: 'Do I get commercial use rights?',
    answer: 'Commercial use rights are included with the Pro plan. Free and Starter plan generations are for personal use only.',
  },
];

// Comparison table data
const COMPARISON_CATEGORIES = [
  {
    name: 'GENERATION',
    rows: [
      { label: 'Monthly credits', free: '50 (one-time)', starter: '500', pro: '1,500' },
      { label: 'Available models', free: 'All models', starter: 'All models', pro: 'All models' },
      { label: 'Generation queue', free: 'Standard', starter: 'Standard', pro: 'Priority' },
      { label: 'Commercial rights', free: false, starter: false, pro: true },
      { label: 'Add-on credits', free: 'Not available', starter: 'Available to purchase', pro: 'Available to purchase' },
    ],
  },
  {
    name: 'FEATURES',
    rows: [
      { label: 'Private mode', free: false, starter: true, pro: true },
      { label: 'Gallery access', free: 'Public only', starter: 'Public + Private', pro: 'Public + Private' },
      { label: 'Character builder', free: true, starter: true, pro: true },
      { label: 'Voice design', free: true, starter: true, pro: true },
      { label: 'Early access to new features', free: false, starter: false, pro: true },
    ],
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-white/5">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-sm font-medium text-white group-hover:text-zinc-200 transition-colors">
          {question}
        </span>
        {isOpen ? (
          <CaretUp className="w-4 h-4 text-zinc-500 shrink-0 ml-4" />
        ) : (
          <CaretDown className="w-4 h-4 text-zinc-500 shrink-0 ml-4" />
        )}
      </button>
      {isOpen && (
        <div className="pb-5 -mt-1">
          <p className="text-sm text-zinc-400 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export function PricingPage({ onNavigate }: PricingPageProps) {
  const { user, creditBalance, createCheckoutSession, openAuthModal } = useAuth();
  const currentTier = user?.subscription_tier || 'free';
  const [showComparison, setShowComparison] = useState(false);

  const handleSubscribe = async (priceId: string | null) => {
    if (!user) {
      openAuthModal();
      return;
    }
    if (!priceId) return;
    await createCheckoutSession(priceId, 'subscription');
  };

  const handleBuyPack = async (priceId: string) => {
    if (!user) {
      openAuthModal();
      return;
    }
    await createCheckoutSession(priceId, 'payment');
  };

  const scrollToFAQ = () => {
    document.getElementById('pricing-faq')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToCompare = () => {
    setShowComparison(true);
    setTimeout(() => {
      document.getElementById('pricing-compare')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  return (
    <div className="min-h-screen bg-neutral-950 overflow-y-auto">
      {/* Gradient aura at top */}
      <div className="relative">
        <div className="absolute inset-0 h-[300px] bg-gradient-to-b from-yellow-500/[0.04] via-transparent to-transparent pointer-events-none" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Choose your plan
          </h1>
          <p className="text-zinc-400 text-sm">
            Select the plan that best fits your needs
          </p>
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {PRICING_TIERS.map(tier => {
            const isCurrent = currentTier === tier.id;
            const isHighlighted = tier.highlighted;

            return (
              <div
                key={tier.id}
                className={`relative rounded-2xl p-6 transition-all ${
                  isHighlighted
                    ? 'border-2 border-yellow-500/50 bg-onyx'
                    : 'border border-white/[0.06] bg-onyx'
                }`}
              >
                {/* Badge */}
                {tier.badge && (
                  <div className="absolute -top-3 right-5">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500 text-black text-[10px] font-bold uppercase tracking-wider">
                      <Crown className="w-3 h-3" weight="fill" />
                      {tier.badge}
                    </span>
                  </div>
                )}

                {/* Plan name & description */}
                <div className="mb-5">
                  <h3 className="text-lg font-semibold text-white mb-1">{tier.name}</h3>
                  <p className="text-xs text-zinc-500">{tier.description}</p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-white">
                      {tier.priceMonthly === 0 ? '$0' : `$${tier.priceMonthly}`}
                    </span>
                    <span className="text-zinc-500 text-sm">/month</span>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="mb-6">
                  {isCurrent ? (
                    <div className="w-full py-3 text-center rounded-xl text-sm font-medium text-zinc-500 bg-white/[0.03] border border-white/[0.06]">
                      Current Plan
                    </div>
                  ) : tier.id === 'free' ? (
                    <div className="w-full py-3 text-center rounded-xl text-sm font-medium text-zinc-500 bg-white/[0.03] border border-white/[0.06]">
                      Current Plan
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSubscribe(tier.priceId)}
                      className={`w-full py-3 rounded-xl text-sm font-semibold transition-all ${
                        isHighlighted
                          ? 'bg-white text-black hover:bg-zinc-100 shadow-lg shadow-white/5'
                          : 'bg-white/10 text-white hover:bg-white/15 border border-white/10'
                      }`}
                    >
                      Subscribe
                    </button>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      {feature.included ? (
                        <Check className="w-4 h-4 text-green-400 shrink-0 mt-0.5" weight="bold" />
                      ) : (
                        <X className="w-4 h-4 text-zinc-600 shrink-0 mt-0.5" weight="bold" />
                      )}
                      <span className={`text-sm leading-snug ${
                        feature.included ? 'text-zinc-300' : 'text-zinc-600'
                      }`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* FAQs / Compare buttons */}
        <div className="flex items-center justify-center gap-3 mb-16">
          <button
            onClick={scrollToFAQ}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 bg-white/[0.03] text-sm text-zinc-300 hover:bg-white/[0.06] transition-colors"
          >
            FAQs <CaretDown className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={scrollToCompare}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 bg-white/[0.03] text-sm text-zinc-300 hover:bg-white/[0.06] transition-colors"
          >
            Compare plans <CaretDown className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Credit Packs */}
        <div className="mb-16">
          <div className="text-center mb-2">
            <h2 className="text-lg font-semibold text-white">Need More?</h2>
          </div>
          <p className="text-center text-xs text-zinc-500 mb-8 max-w-lg mx-auto">
            Credits included in subscriptions do not carry over from month to month.
            Purchased add-on credits do not expire, but require an active subscription to purchase.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {CREDIT_PACKS.map(pack => (
              <div
                key={pack.credits}
                className={`rounded-2xl p-5 border transition-all ${
                  (pack as any).popular
                    ? 'border-yellow-500/30 bg-yellow-500/[0.03]'
                    : 'border-white/[0.06] bg-onyx'
                }`}
              >
                {(pack as any).popular && (
                  <div className="text-[10px] font-bold text-yellow-400 uppercase tracking-wider mb-2">
                    Best Value
                  </div>
                )}
                <div className="flex items-center gap-2 mb-1">
                  <Coins className="w-5 h-5 text-yellow-400" weight="fill" />
                  <span className="text-xl font-bold text-white">{pack.credits.toLocaleString()}</span>
                  <span className="text-zinc-500 text-sm">credits</span>
                </div>
                <div className="text-zinc-600 text-xs mb-4">{pack.perCredit} per credit</div>
                <button
                  onClick={() => handleBuyPack(pack.priceId)}
                  className="w-full py-2.5 rounded-xl text-sm font-medium bg-white/10 text-white hover:bg-white/15 border border-white/10 transition-colors"
                >
                  Buy for {pack.priceLabel}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div id="pricing-faq" className="max-w-2xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-2">FAQs</h2>
          <p className="text-zinc-500 text-sm text-center mb-8">You've got questions. We've got answers.</p>
          <div>
            {FAQ_ITEMS.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        {showComparison && (
          <div id="pricing-compare" className="mb-16">
            <h2 className="text-2xl font-bold text-white text-center mb-10">Compare plans</h2>

            <div className="overflow-x-auto">
              <table className="w-full">
                {/* Header */}
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left py-4 pr-4 w-[30%]" />
                    <th className="text-center py-4 px-4 w-[23%]">
                      <span className="text-sm font-semibold text-white">Free Plan</span>
                    </th>
                    <th className="text-center py-4 px-4 w-[23%]">
                      <span className="text-sm font-semibold text-white">Starter Plan</span>
                    </th>
                    <th className="text-center py-4 px-4 w-[23%]">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-sm font-semibold text-white">Pro Plan</span>
                        <span className="px-2 py-0.5 rounded-full bg-yellow-500 text-black text-[9px] font-bold uppercase">
                          Most Popular
                        </span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_CATEGORIES.map(category => (
                    <React.Fragment key={category.name}>
                      {/* Category header */}
                      <tr>
                        <td
                          colSpan={4}
                          className="pt-6 pb-2 text-[11px] font-bold text-zinc-500 uppercase tracking-wider"
                        >
                          {category.name}
                        </td>
                      </tr>
                      {/* Rows */}
                      {category.rows.map((row, i) => (
                        <tr key={i} className="border-b border-white/[0.03]">
                          <td className="py-3.5 pr-4 text-sm text-zinc-400">{row.label}</td>
                          {(['free', 'starter', 'pro'] as const).map(tier => {
                            const val = row[tier];
                            return (
                              <td key={tier} className="py-3.5 px-4 text-center">
                                {typeof val === 'boolean' ? (
                                  val ? (
                                    <Check className="w-4 h-4 text-green-400 mx-auto" weight="bold" />
                                  ) : (
                                    <X className="w-4 h-4 text-zinc-700 mx-auto" weight="bold" />
                                  )
                                ) : (
                                  <span className="text-sm text-zinc-300">{val}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
